import {App as CapacitorApp} from '@capacitor/app';
import {Browser} from '@capacitor/browser';

type ListenerHandle = { remove: () => void | Promise<void> } | undefined;
type AppPlugin = Pick<typeof CapacitorApp, 'addListener'>;
type BrowserPlugin = Pick<typeof Browser, 'addListener' | 'open' | 'close'>;
type AppReturnHandler = () => Promise<void>;

/**
 * Replaces CordovaPopupNavigator with a Capacitor Browser-based flow.
 * Opens the system browser and resolves when the app is reopened via the auth callback deep link.
 */
export class CapacitorPopupNavigator {
  private appUrlListener?: ListenerHandle;
  private browserFinishedListener?: ListenerHandle;
  private browserFinishedTimer?: ReturnType<typeof setTimeout>;
  private active = false;

  constructor(
    private readonly appReturnHandler: AppReturnHandler = async () => undefined,
    private readonly browserFinishedGracePeriodMs = 1000,
    private readonly appPlugin: AppPlugin = CapacitorApp,
    private readonly browserPlugin: BrowserPlugin = Browser
  ) {}

  async prepare(_: any) {
    return {
      navigate: (navParams: any) => this.open(navParams),
      close: () => this.close()
    };
  }

  // Not used in this flow; required by the interface.
  async callback(_: string, __?: boolean, ___?: string) {
    return;
  }

  private async open(navParams: any) {
    await this.resetActiveSession();
    this.active = true;
    (window as any).__oidcPopupInProgress = true;

    return new Promise<{ url: string }>((resolve, reject) => {
      let settled = false;
      const finish = async (url?: string, error?: any) => {
        if (!this.shouldFinish(settled)) {
          return;
        }
        settled = true;
        this.clearBrowserFinishedTimer();
        await this.cleanup();
        (window as any).__oidcPopupInProgress = false;
        if (url) {
          try {
            await this.appReturnHandler();
          } catch (_) {
            // Loading feedback must not block authentication.
          }
        }
        this.resolveOrReject(resolve, reject, url, error);
      };

      const start = async () => {
        try {
          this.appUrlListener = await this.appPlugin.addListener('appUrlOpen', (event) => {
            if (event?.url) {
              finish(event.url);
            }
          });

          this.browserFinishedListener = await this.browserPlugin.addListener('browserFinished', () => {
            this.clearBrowserFinishedTimer();
            this.browserFinishedTimer = setTimeout(() => {
              void finish(undefined, new Error('Popup closed'));
            }, this.browserFinishedGracePeriodMs);
          });

          await this.browserPlugin.open({
            url: navParams?.url,
            presentationStyle: 'popover'
          });
        } catch (err) {
          finish(undefined, err);
        }
      };

      void start();
    });
  }

  private async cleanup() {
    this.clearBrowserFinishedTimer();
    if (this.appUrlListener?.remove) {
      await this.appUrlListener.remove();
    }
    if (this.browserFinishedListener?.remove) {
      await this.browserFinishedListener.remove();
    }
    this.appUrlListener = undefined;
    this.browserFinishedListener = undefined;
    this.active = false;

    try {
      await this.browserPlugin.close();
    } catch (_) {
      // ignore
    }
  }

  private async close() {
    await this.cleanup();
  }

  private async resetActiveSession() {
    if (this.active) {
      await this.close();
    }
  }

  private shouldFinish(settled: boolean): boolean {
    return !settled;
  }

  private clearBrowserFinishedTimer() {
    if (this.browserFinishedTimer !== undefined) {
      clearTimeout(this.browserFinishedTimer);
      this.browserFinishedTimer = undefined;
    }
  }

  private resolveOrReject(
    resolve: (value: {url: string}) => void,
    reject: (reason?: Error) => void,
    url?: string,
    error?: any
  ) {
    if (url) {
      resolve({url});
      return;
    }
    if (error) {
      reject(error);
      return;
    }
    reject(new Error('Popup closed'));
  }
}
